import React, { useEffect, useState } from 'react';
import relatorioService, { type PrestacaoPcFinanceiro } from '../../services/relatorioPrestacaoContasService';
import { type PrestacaoPc } from '../../services/prestacaoContaService';
import {
  bodyTextClass, formatCurrency, labelTextClass, strongTextClass, subtleSurfaceClass, surfaceClass,
} from './shared';

type Props = {
  planoId: string;
  pcs: PrestacaoPc[];
  /** Muda quando um comprovante com valor (depósito, extrato de aplicação) é anexado ou excluído. */
  reloadKey?: number;
};

const avisoClass = 'rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';

/**
 * Tabela encadeada, somente leitura, com uma linha por PC do plano:
 * saldo = saldo anterior + valor recebido + contrapartida + rendimento − valor utilizado.
 * Nenhum valor é digitado aqui — contrapartida e rendimento vêm dos comprovantes
 * anexados abaixo, e o valor utilizado vem dos pagamentos das etapas.
 */
const PrestacaoExecucaoFinanceiraTable: React.FC<Props> = ({ planoId, pcs, reloadKey }) => {
  const [linhas, setLinhas] = useState<PrestacaoPcFinanceiro[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const temPcFinal = pcs.some((pc) => pc.ehFinal);
  const soma = (campo: keyof Pick<PrestacaoPcFinanceiro, 'rendimentoAplicacao' | 'contrapartida' | 'pagamentosRecursoProprio'>) =>
    linhas.reduce((total, linha) => total + Number(linha[campo] || 0), 0);
  const rendimentoAcumulado = soma('rendimentoAplicacao');
  const contrapartidaAcumulada = soma('contrapartida');
  const recursoProprioPago = soma('pagamentosRecursoProprio');
  const pcsComSaldoNegativo = linhas.filter((l) => Number(l.saldo) < 0);

  useEffect(() => {
    let ativo = true;
    setLoading(true);
    setError(null);
    relatorioService.getFinanceiroConsolidado(planoId)
      .then((dados) => { if (ativo) setLinhas(dados); })
      .catch((err: any) => { if (ativo) setError(err?.response?.data?.error || 'Falha ao carregar a execução financeira.'); })
      .finally(() => { if (ativo) setLoading(false); });
    return () => { ativo = false; };
  }, [planoId, reloadKey]);

  const nomePc = (l: PrestacaoPcFinanceiro) => (l.tipo === 'UNICA' ? 'PC única' : `PC ${l.numero}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className={`text-lg font-semibold ${strongTextClass}`}>Execução Financeira</h2>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          {error}
        </div>
      )}

      {loading && linhas.length === 0 ? (
        <p className={`text-sm ${bodyTextClass}`}>Carregando...</p>
      ) : linhas.length === 0 ? (
        <p className={`text-sm ${labelTextClass}`}>Nenhuma PC criada ainda para este plano.</p>
      ) : (
        <div className={`overflow-x-auto rounded-2xl border ${surfaceClass}`}>
          <table className="w-full text-sm">
            <thead className={subtleSurfaceClass}>
              <tr>
                <th className={`whitespace-nowrap px-2 py-2 align-bottom text-left font-semibold ${labelTextClass}`}>Nº da PC</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Saldo anterior</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Valor recebido</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Contrapartida</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Rendimento de aplicação</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Valor utilizado</th>
                <th className={`px-2 py-2 align-bottom text-right font-semibold ${labelTextClass}`}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => (
                <tr key={l.pcId} className="border-t border-slate-200 dark:border-slate-800">
                  <td className={`whitespace-nowrap px-2 py-2 font-semibold ${strongTextClass}`}>{nomePc(l)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right ${bodyTextClass}`}>{formatCurrency(l.saldoAnterior)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right ${bodyTextClass}`}>{formatCurrency(l.valorRecebido)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right ${bodyTextClass}`}>{formatCurrency(l.contrapartida)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right ${bodyTextClass}`}>{formatCurrency(l.rendimentoAplicacao)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right ${bodyTextClass}`}>{formatCurrency(l.valorUtilizado)}</td>
                  <td className={`whitespace-nowrap px-2 py-2 text-right font-semibold ${Number(l.saldo) < 0 ? 'text-red-600 dark:text-red-300' : strongTextClass}`}>
                    {formatCurrency(l.saldo)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pcsComSaldoNegativo.length > 0 && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200">
          Saldo negativo em {pcsComSaldoNegativo.map(nomePc).join(', ')}: os pagamentos superam os recursos disponíveis na conta.
          Registre o depósito da contrapartida que cobriu a diferença ou revise os pagamentos das etapas.
        </p>
      )}
      {recursoProprioPago > contrapartidaAcumulada && (
        <p className={avisoClass}>
          Pagamentos declarados como recurso próprio somam {formatCurrency(recursoProprioPago)}, mas a contrapartida depositada é de {formatCurrency(contrapartidaAcumulada)}.
          Anexe os comprovantes de depósito da contrapartida.
        </p>
      )}
      {temPcFinal && rendimentoAcumulado > 0 && (
        <p className={avisoClass}>
          Na PC final, os rendimentos acumulados de {formatCurrency(rendimentoAcumulado)} pertencem à Prefeitura e deverão ser devolvidos. A devolução será disponibilizada em uma funcionalidade futura.
        </p>
      )}
    </div>
  );
};

export default PrestacaoExecucaoFinanceiraTable;
